import { useTranslation } from '@packages/utils';
import { TColumnType } from 'components/Base/Table/Table.types';
import {
  EAttendanceType,
  EEventFilterParams,
  EEventSortParams,
  EEventStatus,
  EEventType,
  TEventDataUI,
} from 'data/types/events.types';
import EventStatus from 'components/Shared/Statuses/EventStatus';
import useGetTableColumnData from 'components/Base/Table/components/SearchFilter';
import { Text } from 'components/Base/Typography';
import Tags from 'components/Shared/Tags';
import { TagEllipsis } from 'components/Shared/TagEllipsis';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { ETagsUrls } from 'data/urls/tags.url';
import { ETagType } from 'data/types/tags.types';
import { eventsApi } from 'redux/apiSlice/events.slice';
import ParticipationColumn from 'components/Shared/ParticipationColumn';
import { capacityTextKey } from 'pages/private/Events/hooks/useTranslateCapacityShowValue';
import SessionDates from './components/SessionDates';

const useGetColumnsData = () => {
  const { getTableColumnSearchProps } = useGetTableColumnData();

  const { t } = useTranslation();

  const isTagColumnEditable = useCheckPermission('EVENT_TAG_EDIT');

  const defaultColumns: TColumnType<TEventDataUI>[] = [
    {
      title: t('descriptions.admin-portal.events.list.table.title.CREATE_DATE'),
      dataIndex: 'createdDate',
      key: EEventSortParams.CREATION_DATE,
      sorter: true,
      width: 130,
    },
    {
      title: t('descriptions.admin-portal.events.list.table.title.HOST'),
      dataIndex: 'hostName',
      key: EEventFilterParams.HOST,
      filters: [],
      filterSearch: true,
    },
    {
      title: t('descriptions.admin-portal.events.list.table.title.ELC'),
      dataIndex: 'elcs',
      key: EEventFilterParams.ELC,
      filters: [],
      filterSearch: true,
      width: 180,
      render: (_, record) => {
        if (!record.elcs?.length) {
          return t('descriptions.admin-portal.events.list.table.NOT_IN_ELC');
        }
        return (
          <span
            style={{ display: 'flex', flexWrap: 'wrap', gap: 4, rowGap: 4 }}
          >
            {record.elcs.map(elc => (
              <TagEllipsis
                key={elc.id}
                text={elc.name}
                textMaxLength={20}
                color="grey"
                alwaysShowTooltip
              />
            ))}
          </span>
        );
      },
    },
    {
      title: t('descriptions.admin-portal.events.list.table.title.LOCATION'),
      dataIndex: 'locationName',
      key: EEventFilterParams.LOCATION,
      filters: [],
      filterSearch: true,
      width: 190,
      render: locationName => (
        <Text
          ellipsis={{
            tooltip: locationName,
          }}
          style={{ maxWidth: 150 }}
        >
          {locationName}
        </Text>
      ),
    },
    {
      title: t('descriptions.admin-portal.events.list.table.title.CITY'),
      dataIndex: 'city',
      key: EEventFilterParams.CITY,
      filters: [],
      filterSearch: true,
    },
    {
      title: t('descriptions.admin-portal.events.list.table.title.TYPE'),
      dataIndex: 'eventType',
      key: EEventFilterParams.TYPE,
      filters: [
        ...Object.values(EEventType).map(type => ({
          text:
            type === 'OTHER' ? 'Other' : t(`enums.event-type.${type}`, type),
          value: type,
        })),
      ],
      render: text =>
        text === 'OTHER' ? 'Other' : t(`enums.event-type.${text}`),
    },
    {
      title: t(
        'descriptions.admin-portal.events.list.table.title.TITLE',
        'Title',
      ),
      key: EEventFilterParams.TITLE,
      dataIndex: 'title',
      ...getTableColumnSearchProps<TEventDataUI>(),
    },
    {
      title: t(
        'descriptions.admin-portal.events.list.table.title.COORDINATOR',
        'Coordinator',
      ),
      dataIndex: 'eventCoordinators',
      key: EEventFilterParams.SITE_COORDINATOR,
      ...getTableColumnSearchProps<TEventDataUI>(),
      render: (_, record) => record?.eventCoordinators?.join(', '),
    },
    {
      title: (
        <div style={{ lineHeight: 1.2 }}>
          <div>Participation</div>
          <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.6 }}>
            cap / reg / att
          </div>
        </div>
      ),
      titleLabel: 'Participation',
      dataIndex: 'capacity',
      key: EEventFilterParams.CAPACITY,
      align: 'right',
      width: 132,
      filters: [
        {
          text: 'Near Capacity',
          value: EEventFilterParams.NEAR_CAPACITY,
        },
        {
          text: 'Over Capacity',
          value: EEventFilterParams.OVER_CAPACITY,
        },
      ],
      render: (text, record) => (
        <ParticipationColumn
          capacity={text}
          capacityKeyPrefix={capacityTextKey}
          registrantCount={record.registrantCount}
          attendanceCount={record.attendanceCount}
          hasWaitList={record.hasWaitList}
          isPlanning={record.eventStatus === EEventStatus.PLANNING}
          atCapacityTooltip="At least 1 session in the event is at capacity."
          nearCapacityTooltip="At least 1 session in the event is near capacity."
        />
      ),
    },
    {
      title: t(
        'descriptions.admin-portal.events.list.table.title.EVENT_DATE',
        'Event Date',
      ),
      dataIndex: 'sessionDates',
      // Sorts on the backend's next session date (PART_DATE); the table
      // defaults to this column ascending (GROW-4270).
      key: EEventSortParams.NEXT_SESSION_DATE,
      sorter: true,
      ellipsis: true,
      render: sesionDates => <SessionDates sessionDates={sesionDates} />,
    },
    {
      title: t('descriptions.admin-portal.events.list.table.title.STATUS'),
      dataIndex: 'eventStatus',
      key: EEventFilterParams.STATUS,
      filters: Object.values(EEventStatus).map(status => ({
        text: t(`enums.event-status.${status}`),
        value: status,
      })),
      render: (text: TEventDataUI['eventStatus']) => (
        <EventStatus text={text} />
      ),
    },
    {
      title: t('descriptions.admin-portal.events.list.table.title.ATTENDANCE'),
      dataIndex: 'attendanceTypes',
      key: EEventFilterParams.ATTENDANCE_TYPE,
      filters: Object.values(EAttendanceType).map(type => ({
        text: t(`enums.attendance-type.${type}`),
        value: type,
      })),
      render: (text, record) =>
        t(`enums.attendance-type.${record.attendanceType}`),
    },
    {
      title: t('descriptions.admin-portal.events.list.table.title.TAG', 'Tag'),
      dataIndex: 'tags',
      key: EEventFilterParams.TAGS,
      filters: [],
      filterSearch: true,
      width: 50,
      fixed: 'right',
      onCell: () => ({
        className: 'custom-cell',
        onClick: e => {
          e.stopPropagation();
        },
      }),
      render: (_, record) => (
        <Tags
          selectedTags={record.tags || []}
          associationId={record.eventId}
          url={ETagsUrls.EVENT_EVENT}
          invalidateTagsConfig={[
            { invalidateTag: 'events', sliceName: eventsApi },
          ]}
          tagType={ETagType.EVENT}
          isEditable={isTagColumnEditable}
          isAddDisabled={!isTagColumnEditable}
        />
      ),
    },
  ];
  return { defaultColumns };
};

export default useGetColumnsData;
